/**
 * FooterRail — bottom-of-page nav strip with three jump links.
 *
 * Shown at the foot of every Posture page (Compliance / Security / Identity).
 * Carries:
 *   - "Pillar N of III" eyebrow on the left
 *   - "Back to Command Center" navy chip
 *   - Two cross-pillar jump links to the OTHER two pillars (the current
 *     pillar is excluded — the user is already on it)
 *
 * Visual reference: `.footer-rail` block in 01/02/03 mockups. The 4px double
 * navy top-border is intentionally identical to the standfirst-rule used at
 * the page top — the editorial framing closes where it opened.
 */

import { Link } from 'react-router-dom'
import clsx from 'clsx'
import type { PillarTone } from '@/components/Dashboard/v2/PillarShell'

/** Static metadata about each pillar — single source of truth so the labels,
 *  routes, and accent dots stay in lock-step with the rest of the app. */
const PILLAR_META: Record<PillarTone, {
  /** Roman numeral, "I" / "II" / "III". */
  roman: string
  label: string
  href: string
  /** CSS expression for the small accent dot next to each jump link. */
  swatch: string
}> = {
  compliance: {
    roman: 'I',
    label: 'Pillar I · Compliance',
    href: '/configuration-drift',
    swatch: 'rgb(var(--color-primary-500))',
  },
  bpa: {
    roman: 'II',
    label: 'Pillar II · Security',
    href: '/security-center',
    swatch: 'rgb(var(--color-secondary-blue-300))',
  },
  identity: {
    roman: 'III',
    label: 'Pillar III · Identity',
    href: '/user-insights',
    swatch: 'rgb(var(--color-secondary-sand-300))',
  },
}

interface Props {
  /** The pillar the user is currently on — its jump link is omitted from
   *  the "other pillars" group, and its roman is used in the eyebrow. */
  currentTone: PillarTone
}

/** Render order for the OTHER two pillars given the current one. We hand-pick
 *  the order so adjacent pillars feel visually anchored (Compliance → Security
 *  → Identity), regardless of which page we're on. */
const ORDERED_TONES: PillarTone[] = ['compliance', 'bpa', 'identity']

export default function FooterRail({ currentTone }: Props) {
  const otherPillars = ORDERED_TONES.filter(t => t !== currentTone)
  const current = PILLAR_META[currentTone]

  return (
    <footer
      data-tone={currentTone}
      className="
        mt-14 flex flex-wrap items-center justify-between gap-5
        border-t pt-7 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500
      "
      style={{
        // Same 4px double navy rule as the standfirst — closes the page
        // with the same editorial bracket it opened with.
        borderTopStyle: 'double',
        borderTopWidth: '4px',
        borderTopColor: '#212938',
      }}
    >
      <span>Pillar {current.roman} of III</span>

      <nav
        aria-label="Cross-pillar navigation"
        className="flex flex-wrap gap-2"
      >
        <Link
          to="/dashboard"
          className={clsx(
            'inline-flex items-center gap-2 rounded-full bg-[#212938] px-4 py-2',
            'text-white transition-colors duration-150 hover:bg-dark-700',
          )}
        >
          <span
            aria-hidden
            className="h-2 w-2 rounded-full bg-white"
          />
          Back to Command Center
        </Link>

        {otherPillars.map(toneKey => {
          const meta = PILLAR_META[toneKey]
          return (
            <Link
              key={toneKey}
              to={meta.href}
              className="
                inline-flex items-center gap-2 rounded-full border border-dark-600
                bg-dark-800 px-4 py-2 text-slate-100
                transition-colors duration-150 hover:border-current
                dark:text-slate-100
              "
              style={{ color: 'rgb(var(--color-text-primary))' }}
            >
              <span
                aria-hidden
                className="h-2 w-2 rounded-full"
                style={{ background: meta.swatch }}
              />
              {meta.label}
            </Link>
          )
        })}
      </nav>
    </footer>
  )
}
